import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { ToggleButtonIcon } from './toggle-button-icon';
import { ToggleButtonLabel } from './toggle-button-label';
import { ToggleButtonLoading } from './toggle-button-loading';
import { ToggleButtonRoot } from './toggle-button-root';
import type { ToggleButtonPropsInterface } from './types/toggle-button.types';

/**
 * @since 8.0.0
 */
export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonPropsInterface>(
  (props: ToggleButtonPropsInterface, forwardedRef) => {
    const { asChild = false, children, icon, iconPosition = 'start', ...rest } = props;

    const content = useDecoratedSlotContent<'button'>({
      children,
      asChild,
      template: (childContent) => (
        <Fragment>
          {icon && iconPosition === 'start' ? <ToggleButton.Icon>{icon}</ToggleButton.Icon> : null}
          <ToggleButton.Label>{childContent}</ToggleButton.Label>
          {icon && iconPosition === 'end' ? <ToggleButton.Icon>{icon}</ToggleButton.Icon> : null}
        </Fragment>
      ),
      dependencies: [icon, iconPosition],
    });

    return (
      <ToggleButton.Root ref={forwardedRef} asChild={asChild} {...rest}>
        {content}
      </ToggleButton.Root>
    );
  },
) as ForwardRefExoticComponent<ToggleButtonPropsInterface> & {
  Root: typeof ToggleButtonRoot;
  Label: typeof ToggleButtonLabel;
  Icon: typeof ToggleButtonIcon;
  Loading: typeof ToggleButtonLoading;
};

ToggleButton.displayName = 'ToggleButton';

ToggleButton.Root = ToggleButtonRoot;
ToggleButton.Label = ToggleButtonLabel;
ToggleButton.Icon = ToggleButtonIcon;
ToggleButton.Loading = ToggleButtonLoading;
